import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import {
  Elements,
  PaymentElement,
  useElements,
  useStripe,
} from '@stripe/react-stripe-js'
import { loadStripe, type Stripe } from '@stripe/stripe-js'
import { X } from 'lucide-react'
import { SoftIdentityGate } from '@/components/auth/SoftIdentityGate'
import { Button } from '@/components/ui/button'
import { Spinner } from '@/components/ui/spinner'
import { useAuth } from '@/hooks/use-auth'
import { useLetterConfig } from '@/hooks/use-letter-config'
import { callableErrorMessage } from '@/lib/callable-errors'
import { createLetterPackPaymentIntent } from '@/lib/callables'
import { cn } from '@/lib/utils'
import type { LetterPack } from '@/types/models'

const publishableKey = (import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY as string | undefined) ?? ''
const stripePromise: Promise<Stripe | null> | null = publishableKey
  ? loadStripe(publishableKey)
  : null

function formatPackPrice(cents: number): string {
  return new Intl.NumberFormat('en-AU', {
    style: 'currency',
    currency: 'AUD',
  }).format(cents / 100)
}

function bestValueSku(packs: LetterPack[]): string | null {
  const multipacks = packs.filter((p) => p.letterCount > 1)
  if (multipacks.length === 0) return null
  let best = multipacks[0]
  let bestPer = best.priceCents / best.letterCount
  for (const pack of multipacks.slice(1)) {
    const per = pack.priceCents / pack.letterCount
    if (per < bestPer) {
      best = pack
      bestPer = per
    }
  }
  return best.sku
}

/**
 * Pack picker + Stripe checkout as a sheet/modal (same enter/exit animation
 * as the property profile). Opens above the map / create-Biddle flow.
 */
export function LetterPackPurchaseModal({
  open,
  onClose,
  onPurchased,
}: {
  open: boolean
  onClose: () => void
  /** Fired after payment succeeds and credits appear (or payment confirmed). */
  onPurchased?: () => void
}) {
  const { profile } = useAuth()
  const { config, loading, error } = useLetterConfig()
  const [exiting, setExiting] = useState(false)
  const [visible, setVisible] = useState(open)
  const [selectedSku, setSelectedSku] = useState<string | null>(null)
  const [clientSecret, setClientSecret] = useState<string | null>(null)
  const [busy, setBusy] = useState(false)
  const [actionError, setActionError] = useState<string | null>(null)
  const [notice, setNotice] = useState<string | null>(null)

  const credits = profile?.letterCredits ?? 0
  const creditsBeforePayRef = useRef<number | null>(null)
  const awaitingCreditRef = useRef(false)
  const verified = Boolean(profile?.isIdentityVerified)

  const packs = config.packs
  const selectedPack = useMemo(
    () => packs.find((p) => p.sku === selectedSku) ?? null,
    [packs, selectedSku],
  )
  const bestSku = useMemo(() => bestValueSku(packs), [packs])
  const whitelisted =
    config.whitelistUids.length === 0 ||
    (profile?.id != null && config.whitelistUids.includes(profile.id))

  useEffect(() => {
    if (open) {
      setVisible(true)
      setExiting(false)
      setSelectedSku(null)
      setClientSecret(null)
      setActionError(null)
      setNotice(null)
      awaitingCreditRef.current = false
      creditsBeforePayRef.current = null
    } else if (visible) {
      setExiting(true)
      const t = window.setTimeout(() => {
        setVisible(false)
        setExiting(false)
        setClientSecret(null)
        setSelectedSku(null)
      }, 260)
      return () => window.clearTimeout(t)
    }
  }, [open, visible])

  const requestClose = useCallback(() => {
    if (exiting) return
    onClose()
  }, [exiting, onClose])

  useEffect(() => {
    if (!visible) return
    const prev = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') requestClose()
    }
    window.addEventListener('keydown', onKey)
    return () => {
      document.body.style.overflow = prev
      window.removeEventListener('keydown', onKey)
    }
  }, [visible, requestClose])

  useEffect(() => {
    if (!awaitingCreditRef.current) return
    const before = creditsBeforePayRef.current
    if (before == null) return
    if (credits > before) {
      awaitingCreditRef.current = false
      creditsBeforePayRef.current = null
      setNotice(`Added ${credits - before} credit${credits - before === 1 ? '' : 's'}.`)
      onPurchased?.()
      window.setTimeout(() => requestClose(), 700)
    }
  }, [credits, onPurchased, requestClose])

  async function startCheckout(pack: LetterPack) {
    if (!stripePromise) {
      setActionError('Stripe is not configured (missing VITE_STRIPE_PUBLISHABLE_KEY).')
      return
    }
    setBusy(true)
    setActionError(null)
    setNotice(null)
    setSelectedSku(pack.sku)
    setClientSecret(null)
    try {
      const result = await createLetterPackPaymentIntent(pack.sku)
      setClientSecret(result.clientSecret)
    } catch (e) {
      setActionError(callableErrorMessage(e))
      setSelectedSku(null)
    } finally {
      setBusy(false)
    }
  }

  function onPaymentSucceeded() {
    creditsBeforePayRef.current = credits
    awaitingCreditRef.current = true
    setNotice('Payment successful — updating your balance…')
    setClientSecret(null)
    // If webhook is slow, still notify parent after a short wait so the
    // create-Biddle checkbox can flip when the profile listener updates.
    window.setTimeout(() => {
      if (awaitingCreditRef.current) {
        onPurchased?.()
      }
    }, 2500)
  }

  function backToPacks() {
    setClientSecret(null)
    setSelectedSku(null)
    setActionError(null)
  }

  if (!visible) return null

  const returnUrl = `${window.location.origin}${window.location.pathname}${window.location.search}`

  return createPortal(
    <div
      className={cn(
        'fixed inset-0 z-[70] flex items-end justify-center bg-navy/60 p-0 backdrop-blur-sm sm:items-center sm:p-4',
        exiting ? 'map-sheet-backdrop-exit' : 'map-sheet-backdrop-enter',
      )}
      role="dialog"
      aria-modal="true"
      aria-labelledby="letter-pack-modal-title"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) requestClose()
      }}
    >
      <div
        className={cn(
          'relative flex max-h-[min(92dvh,800px)] w-full max-w-lg flex-col overflow-hidden rounded-t-3xl bg-white shadow-2xl sm:rounded-3xl',
          exiting ? 'property-profile-panel-exit' : 'property-profile-panel-enter',
        )}
      >
        {/* Out of flow so it never shifts header/content padding */}
        <div className="pointer-events-none absolute inset-x-0 top-0 z-20 flex justify-end p-3">
          <button
            type="button"
            onClick={requestClose}
            className="pointer-events-auto rounded-lg p-2 text-muted transition hover:bg-surface hover:text-charcoal"
            aria-label="Close"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5 pt-5">
          {!verified ? (
            <div>
              <SoftIdentityGate
                title="Verify to buy letter credits"
                description="Buying letter packs requires identity verification. It usually takes less than two minutes."
              />
            </div>
          ) : loading ? (
            <div className="flex min-h-[30vh] items-center justify-center">
              <Spinner />
            </div>
          ) : !config.enabled || !whitelisted || packs.length === 0 ? (
            <div className="space-y-2 py-6">
              <h2 id="letter-pack-modal-title" className="text-xl font-bold text-navy">
                Letter packs unavailable
              </h2>
              <p className="text-sm text-muted">
                {error ||
                  (!whitelisted
                    ? 'Letter purchases aren’t available for your account yet.'
                    : 'Physical letters aren’t available right now. Check back soon.')}
              </p>
            </div>
          ) : clientSecret && stripePromise && selectedPack ? (
            <div>
              <button
                type="button"
                onClick={backToPacks}
                className="text-sm font-medium text-biddle-blue hover:underline"
              >
                ← Change pack
              </button>
              <h2 id="letter-pack-modal-title" className="mt-3 text-xl font-bold text-navy">
                Payment
              </h2>
              <p className="mt-1 text-sm text-muted">
                Paying {formatPackPrice(selectedPack.priceCents)} for {selectedPack.letterCount}{' '}
                letter{selectedPack.letterCount === 1 ? '' : 's'}.
              </p>
              <div className="mt-5">
                <Elements stripe={stripePromise} options={{ clientSecret }}>
                  <CheckoutForm
                    returnUrl={returnUrl}
                    onCancel={backToPacks}
                    onSucceeded={onPaymentSucceeded}
                  />
                </Elements>
              </div>
            </div>
          ) : (
            <div>
              <p className="text-sm font-medium text-biddle-blue">Letter credits</p>
              <h2 id="letter-pack-modal-title" className="mt-1 text-xl font-bold text-navy">
                {config.upsell.headline}
              </h2>
              <p className="mt-1 text-sm text-muted">{config.upsell.body}</p>
              <p className="mt-3 text-sm font-semibold text-navy">
                Balance: {credits} credit{credits === 1 ? '' : 's'}
              </p>
              {notice ? <p className="mt-2 text-sm font-medium text-success">{notice}</p> : null}
              <div className="mt-5 space-y-3">
                {packs.map((pack) => {
                  const isBest = pack.sku === bestSku
                  const selected = selectedSku === pack.sku
                  return (
                    <button
                      key={pack.sku}
                      type="button"
                      disabled={busy}
                      onClick={() => void startCheckout(pack)}
                      className={cn(
                        'flex w-full items-center justify-between rounded-xl border px-4 py-3 text-left transition',
                        selected
                          ? 'border-biddle-blue bg-blue-50'
                          : 'border-border hover:border-biddle-blue',
                        busy && 'opacity-60',
                      )}
                    >
                      <div>
                        <div className="flex flex-wrap items-center gap-2">
                          <p className="font-semibold text-navy">
                            {pack.letterCount} letter{pack.letterCount === 1 ? '' : 's'}
                          </p>
                          {isBest ? (
                            <span className="rounded-full bg-navy px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-white">
                              Best value
                            </span>
                          ) : null}
                        </div>
                        <p className="text-xs text-muted">
                          {formatPackPrice(Math.round(pack.priceCents / pack.letterCount))} each
                        </p>
                      </div>
                      <p className="font-semibold text-navy">
                        {formatPackPrice(pack.priceCents)}
                      </p>
                    </button>
                  )
                })}
              </div>
              {busy ? (
                <div className="mt-4 flex items-center gap-2 text-sm text-muted">
                  <Spinner className="h-4 w-4" />
                  Starting checkout…
                </div>
              ) : null}
              {actionError ? <p className="mt-3 text-sm text-danger">{actionError}</p> : null}
            </div>
          )}
        </div>
      </div>
    </div>,
    document.body,
  )
}

function CheckoutForm({
  returnUrl,
  onCancel,
  onSucceeded,
}: {
  returnUrl: string
  onCancel: () => void
  onSucceeded: () => void
}) {
  const stripe = useStripe()
  const elements = useElements()
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  async function onPay(e: React.FormEvent) {
    e.preventDefault()
    if (!stripe || !elements) return
    setBusy(true)
    setError(null)
    try {
      const result = await stripe.confirmPayment({
        elements,
        confirmParams: { return_url: returnUrl },
        redirect: 'if_required',
      })
      if (result.error) {
        setError(result.error.message ?? 'Payment failed')
      } else {
        onSucceeded()
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Payment failed')
    } finally {
      setBusy(false)
    }
  }

  return (
    <form onSubmit={onPay} className="space-y-4">
      <PaymentElement />
      {error ? <p className="text-sm text-danger">{error}</p> : null}
      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" disabled={!stripe || busy}>
          {busy ? <Spinner className="border-white border-t-transparent" /> : null}
          Pay now
        </Button>
        <Button type="button" variant="ghost" disabled={busy} onClick={onCancel}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
